import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import debug from 'debug';
import { Fragment, useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { Button } from '@/components/ui/button';
import { isArabic } from '@/config/constants';
import { isWidgetExpandedState } from '@/state/session';
const log = debug('moveo:card');
export const CarouselCard = ({ card, onPostback, className, disabled, onWebviewClick, onButtonClick, active = false, }) => {
    const { media, buttons, title, subtitle } = card;
    const isWebchatExpanded = useRecoilValue(isWidgetExpandedState);
    const resolvedMedia = useMemo(() => {
        switch (media.type) {
            case 'image':
                return (_jsx("img", { src: media.url, alt: title, className: "h-full w-full object-cover" }));
            case 'video':
                return (_jsx("video", { src: media.url, controls: true, className: "h-full w-full object-cover", children: _jsx("track", { kind: "captions" }) }));
            default:
                return null;
        }
    }, [media.type, media.url, title]);
    const getButtonProps = useCallback((button) => {
        const baseButtonClasses = cn('duration-250 mt-2 inline-block overflow-hidden text-ellipsis whitespace-nowrap text-center transition-colors');
        const handleClick = () => {
            const eventType = `carousel_button_${button.type}`;
            let eventValue;
            switch (button.type) {
                case 'url':
                case 'webview':
                    eventValue = button.url;
                    break;
                case 'postback':
                case 'phone':
                    eventValue = button.value;
                    break;
                default:
                    eventValue = '';
            }
            onButtonClick({
                type: eventType,
                label: button.label,
                value: eventValue,
            });
            if (button.type === 'postback' && onPostback) {
                onPostback(button.value, button.label);
            }
            else if (button.type === 'webview' && onWebviewClick) {
                onWebviewClick(button);
            }
        };
        const buttonProps = {
            dir: isArabic(button.label),
            className: baseButtonClasses,
            size: 'sm',
            disabled,
            children: button.label,
            variant: 'secondary',
        };
        switch (button.type) {
            case 'postback':
                return _jsx(Button, Object.assign({}, buttonProps, { onClick: handleClick }));
            case 'url':
                return (_jsx(Button, Object.assign({}, buttonProps, { onClick: (e) => {
                        e.preventDefault();
                        window.open(button.url, '_blank', 'noreferrer');
                        handleClick();
                    } })));
            case 'phone':
                return (_jsx(Button, Object.assign({}, buttonProps, { onClick: (e) => {
                        e.preventDefault();
                        window.location.href = `tel:${button.value}`;
                        handleClick();
                    } })));
            case 'webview': {
                if (button.height === 'new_window') {
                    return (_jsx(Button, Object.assign({}, buttonProps, { asChild: true, onClick: handleClick, children: _jsx("a", { href: button.url, target: "_blank", rel: "noopener noreferrer", children: button.label }) })));
                }
                return _jsx(Button, Object.assign({}, buttonProps, { onClick: handleClick }));
            }
            default:
                log(`Unsupported button type: ${button}`, button);
                return null;
        }
    }, [disabled, onButtonClick, onPostback, onWebviewClick]);
    return (_jsxs("div", { className: cn('flex h-full flex-col justify-between overflow-hidden rounded-xl border border-carousel-card-border bg-carousel-card text-carousel-card-foreground', {
            'opacity-30': !active,
        }, className), children: [_jsxs("div", { className: "grow", children: [_jsx("div", { className: cn('h-40 w-full', {
                            'h-45': isWebchatExpanded,
                        }), children: resolvedMedia }), _jsxs("div", { className: "px-4 pt-2", children: [_jsx("h4", { className: "mb-1.5 line-clamp-2 overflow-hidden text-lg font-medium leading-7 text-carousel-card-foreground", children: title }), _jsx("p", { className: "text-carousel-card-foreground/70 m-0 text-sm leading-5", children: subtitle })] })] }), _jsx("div", { className: "mt-1.5 flex flex-col justify-end px-4 pb-4", children: buttons.map((button, i) => (_jsx(Fragment, { children: getButtonProps(button) }, `${i}_${button.type}`))) })] }));
};
